import React, { useState, useEffect, useCallback } from 'react'
import axios from 'axios'

import StatTiles from './components/stat_tiles'
import AccountTable from './components/account_table'
import { fetchAccountStats, fetchAccountDaily, sendTestEmail } from './helpers/account_monitoring_service'
import { pluralMailboxes } from './helpers/helper'

import '../../static/css/account_monitoring.css'

/* Account Monitoring — every connected email account, its recent send volume, whether it
 * can send right now, an on-demand 7-day breakdown and a per-row real test send.
 *
 * Routed under /admin rather than /{client}: the endpoints take no client argument.
 */
const AccountMonitoring = () => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [expanded, setExpanded] = useState(null)
  const [daily, setDaily] = useState({})
  const [confirming, setConfirming] = useState(null)
  const [sending, setSending] = useState(null)
  const [sendResult, setSendResult] = useState({})
  const [reloadKey, setReloadKey] = useState(0)

  // Loads /stats. Keyed on reloadKey alone — there is deliberately NO polling timer:
  // /stats is a live fan-out across every mailbox (seconds, not milliseconds), and a
  // background refresh would hammer EmailEngine for a table nobody is looking at.
  useEffect(() => {
    const controller = new AbortController()

    // Step 1: drop cached daily strips and collapse the open row. Without this, a Refresh
    // an hour later updates the table while the expanded strip keeps rendering hour-old
    // numbers beside fresh ones — two vintages in one view, with nothing saying so.
    setDaily({})
    setExpanded(null)
    setLoading(true)

    // Step 2: fetch the snapshot.
    fetchAccountStats(controller.signal)
      .then((stats) => {
        setData(stats)
        setError(null)
        setLoading(false)
      })
      .catch((err) => {
        // Step 3: an abort is not a failure — leave every piece of state alone, `loading`
        // included. StrictMode double-mounts in dev, so the first request aborts itself and
        // a second starts; clearing state here would flash "canceled" over a blanked table
        // on every mount. A genuine failure clears `data` so no stale table sits under the
        // error message.
        if (axios.isCancel(err)) return
        setData(null)
        setError(err.message)
        setLoading(false)
      })

    return () => controller.abort('canceled')
  }, [reloadKey])

  // Expands a row, fetching its 7-day strip only the first time it opens.
  const onToggleDaily = useCallback((account) => {
    // Step 1: collapsing needs no request, and the cache is kept so re-expanding is instant.
    if (expanded === account) {
      setExpanded(null)
      return
    }
    setExpanded(account)

    // Step 2: serve a cached account from state. A 7-day pull costs ~7s — one IMAP search
    // per day, necessarily sequential because EmailEngine serialises work per account — so
    // re-fetching on every expand is not acceptable.
    if (daily[account]) return

    // Step 3: fetch, recording the outcome under this account only. No abort signal is
    // passed: a cancelled fetch would have to be written into the cache as an error, and
    // the fetch-once rule would then pin "canceled" on that row permanently.
    setDaily((prev) => ({ ...prev, [account]: { loading: true } }))
    fetchAccountDaily(account)
      .then((res) => {
        setDaily((prev) => ({ ...prev, [account]: { items: res.items, path: res.path } }))
      })
      .catch((err) => {
        setDaily((prev) => ({ ...prev, [account]: { error: err.message } }))
      })
  }, [expanded, daily])

  // Two-step arm: the first click only arms the button, the second sends real mail.
  const onTestSend = useCallback((account) => {
    // Step 1: first click arms this row and sends nothing.
    if (confirming !== account) {
      setConfirming(account)
      return
    }

    // Step 2: second click sends. Disarm first so a stray double-fire cannot re-enter.
    setConfirming(null)
    setSending(account)

    // Step 3: record the outcome against this account only, so other rows are undisturbed.
    // A state write after unmount is a silent no-op in React 18, so the in-flight send
    // needs no mounted-ref guard.
    sendTestEmail(account)
      .then((res) => {
        setSendResult((prev) => ({
          ...prev,
          // "Accepted for delivery", not "sent": EmailEngine accepting the message does not
          // mean it arrived. A rate-limited account can accept and fail minutes later, and
          // that failure shows up in the Can send column on the next Refresh.
          [account]: { ok: true, message: `Accepted for delivery to ${res.to || 'the test recipient'}` }
        }))
      })
      .catch((err) => {
        setSendResult((prev) => ({ ...prev, [account]: { ok: false, message: err.message } }))
      })
      .finally(() => setSending(null))
  }, [confirming])

  // Blurring an armed button disarms it, so a button left alone goes cold.
  const onDisarmTestSend = useCallback(() => setConfirming(null), [])

  const degraded = (data && data.degraded) || 0
  const blocked = (data && data.blocked) || 0
  const items = (data && data.items) || []

  return (
    <div className='cocrm-account-monitoring overflow-auto'>
      <div>
        <h1 className='co-page-title'>Account Monitoring</h1>
        <p className='co-page-sub'>Connected email accounts &mdash; recent send volume, send health and a per-mailbox test send.</p>
      </div>
      {/* Toolbar: manual refresh only, plus the two fleet-level warning counters. */}
      <div className='am-toolbar'>
        <div className='am-toolbar-left'>
          <button
            type='button'
            className='am-refresh-btn'
            title='Refresh'
            aria-label='Refresh'
            disabled={loading}
            onClick={() => setReloadKey((key) => key + 1)}
          >
            <i className={'fa-solid fa-rotate-right' + (loading ? ' fa-spin' : '')} />
            <span>Refresh</span>
          </button>
          {degraded > 0 ? (
            <span className='am-badge am-badge-warning'>{`${pluralMailboxes(degraded)} unreadable`}</span>
          ) : null}
          {blocked > 0 ? (
            <span className='am-badge am-badge-danger'>{`${pluralMailboxes(blocked)} cannot send`}</span>
          ) : null}
        </div>
        <span className='am-toolbar-count'>{pluralMailboxes(data ? data.total : 0)}</span>
      </div>
      <StatTiles
        data={data}
        loading={loading}
      />
      <AccountTable
        items={items}
        loading={loading}
        error={error}
        expanded={expanded}
        daily={daily}
        confirming={confirming}
        sending={sending}
        sendResult={sendResult}
        onToggleDaily={onToggleDaily}
        onTestSend={onTestSend}
        onDisarmTestSend={onDisarmTestSend}
      />
    </div>
  )
}

export default AccountMonitoring
